import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { unifiedColors } from 'styles/colors'
import { media } from 'styles/responsive'
import { theme, themeUtils } from 'styles/theme'

type StyledInputProps = {
  error?: boolean
  lowercase?: boolean
  uppercase?: boolean
  noPadding?: boolean
}

type TextInputWrapperProps = {
  hasLeftSymbol?: boolean
  error?: boolean
  noRadius?: boolean
  noFocus?: boolean
  disabled: boolean
  $whiteBackground?: boolean
  noBorder?: boolean
}

export const TextInputWrapper = styled.div<TextInputWrapperProps>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  border-radius: 12px;
  ${({ noRadius }) =>
    noRadius &&
    css`
      border-radius: 0px;
    `}
  border: ${({ noBorder }) =>
    noBorder ? 'none' : `1px solid ${theme.palette.surface.secondary}`};
  line-height: 16px;
  ${({ noFocus }) =>
    !noFocus &&
    css`
      &:focus-within {
        outline: 2px solid ${unifiedColors.button.primary.hovered};
      }
    `}

  ${({ hasLeftSymbol }) =>
    hasLeftSymbol &&
    css`
      padding: 12px;
    `}
  ${({ error }) =>
    error &&
    css`
      background-color: ${theme.palette.surface.red};
      border-color: ${theme.palette.border.focused.error};
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${theme.palette.surface.subdued};
      border-color: ${theme.palette.surface.secondary};
      color: ${theme.palette.label.subdued};
      cursor: not-allowed;
    `}
  background: ${({ $whiteBackground }) =>
    $whiteBackground ? theme.palette.surface.default : 'none'};
`

export const SymbolContainer = styled.div`
  border-radius: 2px;
  border: 1px solid rgba(40, 37, 59, 0.08);
  background: ${theme.palette.surface.subdued};
  display: flex;
  padding: 1px 4px;
  align-items: flex-start;
  color: ${theme.palette.label.subdued};
  ${themeUtils.body}
  ${media.minWidth.md} {
    ${themeUtils.bodySmall};
  }
`

export const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid ${theme.palette.surface.secondary};
  padding: 0 14px;
  ${themeUtils.body}
  ${media.minWidth.md} {
    ${themeUtils.bodySmall};
  }
  line-height: 16px;
  ${({ lowercase }) =>
    lowercase &&
    css`
      text-transform: lowercase;
    `}
  ${({ uppercase }) =>
    uppercase &&
    css`
      text-transform: uppercase;
    `}
  ${({ error }) =>
    error &&
    css`
      background-color: ${theme.palette.surface.red};
      border-color: ${theme.palette.border.focused.error};
    `}
`

export const StyledTextInput = styled.input<StyledInputProps>`
  border: none;
  padding: ${({ noPadding }) => (noPadding ? '0' : '14px')};
  width: 100%;
  ${themeUtils.body}
  ${media.minWidth.md} {
    ${themeUtils.bodySmall};
  }
  background: none;
  outline: none;
  line-height: 16px;
  ${({ lowercase }) =>
    lowercase &&
    css`
      text-transform: lowercase;
    `}
  ${({ uppercase }) =>
    uppercase &&
    css`
      text-transform: uppercase;
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      color: ${theme.palette.label.subduedDark} !important;
      cursor: not-allowed;
    `}
`

export const TextAreaInputWrapper = styled.div<TextInputWrapperProps>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  height: fit-content;
  border-radius: 12px;
  border: 1px solid ${theme.palette.surface.secondary};
  ${({ noFocus }) =>
    !noFocus &&
    css`
      &:focus-within {
        outline: 2px solid ${unifiedColors.button.primary.hovered};
      }
    `}
  ${({ hasLeftSymbol }) =>
    hasLeftSymbol &&
    css`
      padding: 12px;
    `}
  ${({ error }) =>
    error &&
    css`
      background-color: ${theme.palette.surface.red};
      border-color: ${theme.palette.border.focused.error};
    `}
  background: ${({ $whiteBackground }) =>
    $whiteBackground ? theme.palette.surface.default : 'none'};
`

export const StyledTextAreaInput = styled.textarea<StyledInputProps>`
  border: none;
  width: 100%;
  min-height: 44px;
  resize: vertical;
  height: 100%;
  padding: 14px;
  ${themeUtils.body}
  ${media.minWidth.md} {
    ${themeUtils.bodySmall};
  }
  background: none;
  outline: none;
  line-height: 16px;
  ${({ lowercase }) =>
    lowercase &&
    css`
      text-transform: lowercase;
    `}
  ${({ uppercase }) =>
    uppercase &&
    css`
      text-transform: uppercase;
    `}
`

export const InputErrorLabel = styled.p<{ padded?: boolean }>`
  margin-top: 4px;
  ${({ padded }) =>
    padded &&
    css`
      padding: 0 6px;
    `}
  ${themeUtils.bodyXSmall}
  color: #b50000;
`

export const InputLabel = styled.label`
  ${themeUtils.label}
  margin-bottom: 4px;
  display: flex;
  gap: 8px;
`

export const OptionalLabel = styled.div`
  background-color: ${theme.palette.surface.secondary};
  ${themeUtils.bodyXSmallSemiBoldGrey};
  padding: 2px 5px;
  border: none;
  border-radius: 4px;
  margin-bottom: 4px;
  height: fit-content;
`

type InputLabelBlockProps = {
  fullSize?: boolean
  noMargin?: boolean
}

export const InputLabelBlock = styled.div<InputLabelBlockProps>`
  margin-bottom: ${({ noMargin }) => (noMargin ? '0px' : '16px')};
  width: 100%;
  ${media.minWidth.md} {
    ${({ fullSize }) => css`
      width: ${fullSize ? '100%' : '48%;'};
    `}
  }
  ${({ noMargin }) =>
    noMargin &&
    css`
      margin-bottom: 0px;
    `}
`

export const CheckButton = styled.button<{ isFinished?: boolean }>`
  width: 70px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;
  ${themeUtils.bodySmall}
  ${({ isFinished }) =>
    isFinished &&
    css`
      background-color: ${theme.palette.surface.green};
    `}
`
